import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { NeptuneLogo } from "./NeptuneMark";
import { useContent } from "../../context/ContentContext";
import { useMerchantAuth } from "../../context/MerchantAuthContext";

const linksConfig = [
  { key: "nav.home", to: "/", fallback: "Home" },
  { key: "nav.solutions", to: "/solutions", fallback: "Solutions" },
  { key: "nav.industries", to: "/industries", fallback: "Industries" },
  { key: "nav.about", to: "/about", fallback: "About" },
  { key: "nav.contact", to: "/contact", fallback: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useContent();
  const { user: merchant } = useMerchantAuth();

  const links = linksConfig.map((l) => ({ ...l, label: t(l.key, l.fallback) }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Lock body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled || open
            ? "bg-[#0a0a0a]/70 backdrop-blur-2xl border-b border-white/[0.06]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center group relative z-[60]"
          >
            <NeptuneLogo height={26} />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => {
                const active = pathname === l.to;
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-link-${l.label.toLowerCase()}`}
                    className={`relative text-[13.5px] py-1 transition-colors group ${
                      active ? "text-white" : "text-zinc-400 hover:text-white"
                    }`}
                  >
                    {l.label}
                    {/* Active / hover underline */}
                    <span
                      className={`absolute left-0 -bottom-0.5 h-px w-full origin-left transition-transform duration-500 ease-out bg-gradient-to-r from-[#00E5FF] to-[#0055FF] ${
                        active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      }`}
                    />
                    {active && (
                      <motion.span
                        layoutId="nav-glow"
                        className="absolute -inset-x-3 inset-y-0 -z-10 rounded-full bg-white/[0.04]"
                        transition={{ type: "spring", stiffness: 380, damping: 32 }}
                      />
                    )}
                  </Link>
                );
              })}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {merchant ? (
              <Link
                to="/app"
                data-testid="navbar-open-app-btn"
                className="group relative inline-flex items-center justify-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 transition-colors px-4 py-2 text-[13px] font-medium overflow-hidden"
              >
                <span className="relative z-10 inline-flex items-center gap-1.5">
                  Open app
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
                <span className="pointer-events-none absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_50%,rgba(0,229,255,0.35),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500 mix-blend-screen" />
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="navbar-login-btn"
                  className="group inline-flex items-center justify-center gap-1.5 rounded-full border border-white/[0.12] bg-white/[0.02] text-white hover:bg-white/[0.06] hover:border-white/[0.22] transition-colors px-4 py-2 text-[13px] font-medium"
                >
                  Log in
                </Link>
                <Link
                  to="/signup"
                  data-testid="navbar-get-started-btn"
                  className="group relative inline-flex items-center justify-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 transition-colors px-4 py-2 text-[13px] font-medium overflow-hidden"
                >
                  <span className="relative z-10 inline-flex items-center gap-1.5">
                    {t("nav.cta", "Get Started")}
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                  <span className="pointer-events-none absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_50%,rgba(0,229,255,0.35),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500 mix-blend-screen" />
                </Link>
              </>
            )}
          </div>

          <button
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="navbar-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden relative z-[60] inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/10 bg-white/[0.02] backdrop-blur-md text-white/90 hover:bg-white/[0.06] transition-colors"
          >
            <AnimatePresence mode="wait" initial={false}>
              {open ? (
                <motion.span
                  key="x"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute"
                >
                  <X className="h-4 w-4" />
                </motion.span>
              ) : (
                <motion.span
                  key="m"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute"
                >
                  <Menu className="h-4 w-4" />
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </header>

      {/* Cinematic fullscreen mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 md:hidden bg-[#0a0a0a] overflow-hidden"
            data-testid="mobile-menu"
          >
            {/* Ambient glows */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[600px] w-[700px] rounded-full bg-[#0055FF]/30 blur-[140px]"
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.4, delay: 0.1 }}
              className="pointer-events-none absolute bottom-[-20%] right-[-10%] h-[460px] w-[460px] rounded-full bg-[#00E5FF]/10 blur-[140px]"
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.4, delay: 0.2 }}
              className="pointer-events-none absolute top-[40%] left-[-15%] h-[400px] w-[400px] rounded-full bg-[#0055FF]/15 blur-[140px]"
            />

            <div className="relative h-full flex flex-col pt-24 pb-10 px-7">
              {/* Eyebrow */}
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.5 }}
                className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-10"
              >
                — Menu
              </motion.div>

              {/* Big editorial nav */}
              <nav className="flex flex-col gap-1.5">
                {links.map((l, i) => {
                  const active = pathname === l.to;
                  return (
                    <motion.div
                      key={l.to}
                      initial={{ opacity: 0, y: 28, filter: "blur(8px)" }}
                      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                      exit={{ opacity: 0, y: 14, filter: "blur(6px)" }}
                      transition={{
                        delay: 0.18 + i * 0.07,
                        duration: 0.7,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                    >
                      <Link
                        to={l.to}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                        className="group flex items-baseline justify-between border-b border-white/[0.06] py-5"
                      >
                        <span className="flex items-baseline gap-4">
                          <span className="font-mono text-[11px] text-zinc-600 group-hover:text-[#00E5FF] transition-colors">
                            0{i + 1}
                          </span>
                          <span
                            className={`font-display text-[40px] sm:text-5xl font-semibold tracking-tighter leading-none transition-colors ${
                              active
                                ? "chrome-text"
                                : "text-white group-hover:text-white"
                            }`}
                          >
                            {l.label}
                          </span>
                        </span>
                        <ArrowUpRight
                          className="h-5 w-5 text-zinc-600 group-hover:text-[#00E5FF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                          strokeWidth={1.5}
                        />
                      </Link>
                    </motion.div>
                  );
                })}
              </nav>

              {/* Get Started — pinned cinematic block */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 0.18 + links.length * 0.07,
                  duration: 0.7,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="mt-auto"
              >
                {merchant ? (
                  <Link
                    to="/app"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-open-app-btn"
                    className="group w-full inline-flex items-center justify-between rounded-2xl bg-white text-black px-5 py-4 text-[15px] font-medium hover:bg-zinc-200 transition-colors"
                  >
                    <span>Open app</span>
                    <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setOpen(false)}
                      data-testid="mobile-login-btn"
                      className="group w-full inline-flex items-center justify-between rounded-2xl border border-white/[0.12] bg-white/[0.02] text-white px-5 py-4 text-[15px] font-medium hover:bg-white/[0.06] transition-colors mb-3"
                    >
                      <span>Log in</span>
                      <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </Link>
                    <Link
                      to="/signup"
                      onClick={() => setOpen(false)}
                      data-testid="mobile-get-started-btn"
                      className="group w-full inline-flex items-center justify-between rounded-2xl bg-white text-black px-5 py-4 text-[15px] font-medium hover:bg-zinc-200 transition-colors"
                    >
                      <span>{t("nav.cta", "Get Started")}</span>
                      <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </Link>
                  </>
                )}
                <div className="mt-6 flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-zinc-600">
                  <span>hello@neptunepay.io</span>
                  <span>v3</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
